import React, { useState, useEffect } from 'react';
import { Box, Alert, Chip } from '@mui/material';
import {
  ViiluDataGrid, PageHeader, PageContent, useRouter,
  RecordState, getRecordStateLabel, getRecordStateColor, getEnumValueOptions,
} from '@viilutalous/common';
import { departmentsAPI } from './employeesAPI';
import { useTranslation } from 'react-i18next';

export default function Departments({ tenantId }) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      fetchDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchDepartments = async () => {
    if (!tenantId) return;

    try {
      const response = await departmentsAPI.getAll(tenantId);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    navigate('/departments/new');
  };

  const handleEdit = (department) => {
    navigate(`/departments/${department.id}`);
  };

  const handleDelete = async (id) => {
    if (!tenantId) return;

    if (window.confirm(t('departments.delete_confirm'))) {
      try {
        await departmentsAPI.delete(tenantId, id);
        fetchDepartments();
      } catch (error) {
        console.error('Error deleting department:', error);
      }
    }
  };

  const getContextMenuItems = (params) => {
    const result = [
      {
        name: t('common.new'),
        action: () => navigate('/departments/new'),
        icon: '<i class="ag-icon ag-icon-plus"/>',
        shortcut: 'Ins',
      },
    ];

    if (!params.node) return result;

    const department = params.node.data;
    result.push('separator');
    result.push({
      name: t('common.edit'),
      action: () => handleEdit(department),
      icon: '<i class="ag-icon ag-icon-edit"/>',
      shortcut: 'Enter',
    });

    result.push('separator');
    result.push({
      name: t('common.delete'),
      action: () => handleDelete(department.id),
      icon: '<i class="ag-icon ag-icon-cancel"/>',
      cssClasses: ['ag-menu-option-text-danger'],
    });

    return result;
  };

  const StateCellRenderer = ({ value }) => (
    <Chip
      label={t(`common.state.${getRecordStateLabel(value)}`)}
      color={getRecordStateColor(value)}
      size="small"
    />
  );

  const columnDefs = [
    { field: 'name', headerName: t('departments.name'), flex: 1, minWidth: 200 },
    {
      field: 'state',
      headerName: t('departments.state'),
      width: 120,
      cellRenderer: StateCellRenderer,
      valueOptions: getEnumValueOptions(RecordState, getRecordStateLabel, t, 'common.state'),
    },
  ];

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('departments.select_tenant')}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <ViiluDataGrid
          tenantId={tenantId}
          gridId="departments-list"
          rowData={departments}
          columnDefs={columnDefs}
          loading={loading}
          onRowDoubleClicked={(event) => navigate(`/departments/${event.data.id}`)}
          getContextMenuItems={getContextMenuItems}
          onNew={handleNew}
          onEdit={(row) => navigate(`/departments/${row.id}`)}
          onRefresh={fetchDepartments}
        />
      </PageContent>
    </>
  );
}
